var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { EMessagePurpose, EMessagingProvider, EProviderApproval, MESSAGE_PURPOSES, } from "@/types/messaging";
import { accountCapabilities, accountIdentity, checkedForAnotherIdentity, isUnsupportedProvider, providerCapabilities, providersForChannel, purposeReadiness, requiredCredentials, typedCredentials, } from "@/services/messaging";
export var DEFAULT_META_API_VERSION = "v23.0";
var field = function (key, required) {
    if (required === void 0) { required = false; }
    return ({ key: key, required: required });
};
/** Each provider's non-secret identifiers, in the order the drawer shows them. */
export var senderFields = function (provider) {
    switch (provider) {
        case EMessagingProvider.AWS_SES:
            return [
                field("from_address", true),
                field("from_name"),
                field("region"),
                field("notification_topic_arn"),
            ];
        case EMessagingProvider.SMTP:
            return [field("from_address", true), field("from_name"), field("server_url", true)];
        case EMessagingProvider.AWS_SNS:
            return [field("sender_id"), field("origination_number"), field("region")];
        case EMessagingProvider.WHATSAPP_CLOUD_API:
            return [
                field("business_account_id", true),
                field("phone_number_id", true),
                field("display_phone_number", true),
                field("display_name"),
                field("api_version", true),
                field("api_base_url"),
            ];
        case EMessagingProvider.MESSENGER_SEND_API:
            return [
                field("page_id", true),
                field("page_name"),
                field("page_username"),
                field("api_version", true),
                field("api_base_url"),
            ];
        case EMessagingProvider.VIBER_INFOBIP:
            return [field("base_url", true), field("sender", true)];
        case EMessagingProvider.HTTP_API:
        case EMessagingProvider.CONSOLE:
            // Unsupported: nothing to edit; the account is shown to be replaced.
            return [];
    }
};
/** Providers an administrator can pick for a new account of a channel: its own adapters. */
export var selectableProviders = function (channel) {
    return providersForChannel(channel);
};
var initialSender = function (provider) {
    return Object.fromEntries(senderFields(provider).map(function (_a) {
        var key = _a.key;
        return [
            key,
            key === "api_version" ? DEFAULT_META_API_VERSION : "",
        ];
    }));
};
export var emptyAccountForm = function (channel, provider) { return ({
    channel: channel,
    provider: provider,
    name: "",
    sender: initialSender(provider),
    viberTemplates: [],
    limits: {
        messagesPerSecond: "",
        otpReservedPerSecond: "",
        otpConcurrency: "",
        allowedCallingCodes: "",
    },
    providerApproval: EProviderApproval.PENDING,
    isDefault: false,
    credentials: {},
}); };
/** Switches the form to another provider, keeping what does not depend on it. */
export var withProvider = function (values, provider) { return (__assign(__assign({}, values), { provider: provider, sender: initialSender(provider), viberTemplates: [], credentials: {} })); };
/**
 * Switches a new account to another channel. A provider of that channel is
 * kept with what was entered; otherwise the channel's first provider is
 * selected.
 */
export var withChannel = function (values, channel) {
    return providerCapabilities(values.provider, channel)
        ? __assign(__assign({}, values), { channel: channel }) : withProvider(__assign(__assign({}, values), { channel: channel }), selectableProviders(channel)[0]);
};
var senderValues = function (sender) {
    var values = {};
    var record = sender;
    for (var _i = 0, _a = senderFields(sender.provider); _i < _a.length; _i++) {
        var key = _a[_i].key;
        var value = record[key];
        values[key] = typeof value === "string" ? value : "";
    }
    return values;
};
var viberRows = function (templates) {
    return MESSAGE_PURPOSES.flatMap(function (purpose) {
        var _a;
        return Object.entries((_a = templates === null || templates === void 0 ? void 0 : templates[purpose]) !== null && _a !== void 0 ? _a : {}).map(function (_a) {
            var language = _a[0], templateId = _a[1];
            return ({
                purpose: purpose,
                language: language,
                templateId: templateId,
            });
        });
    });
};
var countText = function (value) {
    return value === null || value === undefined ? "" : String(value);
};
export var formFromAccount = function (account) {
    var _a, _b, _c, _d, _e, _f;
    return ({
        channel: account.channel,
        provider: account.provider,
        name: account.name,
        sender: senderValues(account.sender),
        viberTemplates: account.sender.provider === EMessagingProvider.VIBER_INFOBIP
            ? viberRows(account.sender.approved_templates)
            : [],
        limits: {
            messagesPerSecond: countText((_a = account.limits) === null || _a === void 0 ? void 0 : _a.messages_per_second),
            otpReservedPerSecond: countText((_b = account.limits) === null || _b === void 0 ? void 0 : _b.otp_reserved_per_second),
            otpConcurrency: countText((_c = account.limits) === null || _c === void 0 ? void 0 : _c.otp_concurrency),
            allowedCallingCodes: ((_e = (_d = account.limits) === null || _d === void 0 ? void 0 : _d.allowed_calling_codes) !== null && _e !== void 0 ? _e : []).join(", "),
        },
        providerApproval: (_f = account.provider_approval) !== null && _f !== void 0 ? _f : EProviderApproval.PENDING,
        isDefault: account.is_default,
        credentials: {},
    });
};
var CALLING_CODE = /^\+?(\d{1,3})$/;
/** Country calling codes, as digits; empty means any destination. */
export var parseCallingCodes = function (text) {
    var codes = [];
    var invalid = [];
    for (var _i = 0, _a = text.split(/[\s,;]+/).filter(Boolean); _i < _a.length; _i++) {
        var entry = _a[_i];
        var match = CALLING_CODE.exec(entry);
        if (!match) {
            invalid.push(entry);
        }
        else if (!codes.includes(match[1])) {
            codes.push(match[1]);
        }
    }
    return { codes: codes, invalid: invalid };
};
var required = function (values, key) { var _a; return ((_a = values.sender[key]) !== null && _a !== void 0 ? _a : "").trim(); };
var optional = function (values, key) {
    return required(values, key) || null;
};
var viberTemplates = function (rows) {
    var _a;
    var _b;
    var templates = {};
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        var language = row.language.trim();
        var templateId = row.templateId.trim();
        if (!language || !templateId) {
            continue;
        }
        templates[row.purpose] = __assign(__assign({}, ((_b = templates[row.purpose]) !== null && _b !== void 0 ? _b : {})), (_a = {}, _a[language] = templateId, _a));
    }
    return templates;
};
export var senderFromForm = function (values) {
    switch (values.provider) {
        case EMessagingProvider.AWS_SES:
            return {
                provider: EMessagingProvider.AWS_SES,
                from_address: required(values, "from_address"),
                from_name: optional(values, "from_name"),
                region: optional(values, "region"),
                notification_topic_arn: optional(values, "notification_topic_arn"),
            };
        case EMessagingProvider.SMTP:
            return {
                provider: EMessagingProvider.SMTP,
                from_address: required(values, "from_address"),
                from_name: optional(values, "from_name"),
                server_url: required(values, "server_url"),
            };
        case EMessagingProvider.AWS_SNS:
            return {
                provider: EMessagingProvider.AWS_SNS,
                sender_id: optional(values, "sender_id"),
                origination_number: optional(values, "origination_number"),
                region: optional(values, "region"),
            };
        case EMessagingProvider.WHATSAPP_CLOUD_API:
            return {
                provider: EMessagingProvider.WHATSAPP_CLOUD_API,
                business_account_id: required(values, "business_account_id"),
                phone_number_id: required(values, "phone_number_id"),
                display_phone_number: required(values, "display_phone_number"),
                display_name: optional(values, "display_name"),
                api_version: required(values, "api_version"),
                api_base_url: optional(values, "api_base_url"),
            };
        case EMessagingProvider.MESSENGER_SEND_API:
            return {
                provider: EMessagingProvider.MESSENGER_SEND_API,
                page_id: required(values, "page_id"),
                page_name: optional(values, "page_name"),
                page_username: optional(values, "page_username"),
                api_version: required(values, "api_version"),
                api_base_url: optional(values, "api_base_url"),
            };
        case EMessagingProvider.VIBER_INFOBIP:
            return {
                provider: EMessagingProvider.VIBER_INFOBIP,
                base_url: required(values, "base_url"),
                sender: required(values, "sender"),
                approved_templates: viberTemplates(values.viberTemplates),
            };
        case EMessagingProvider.HTTP_API:
            return { provider: EMessagingProvider.HTTP_API };
        case EMessagingProvider.CONSOLE:
            return { provider: EMessagingProvider.CONSOLE };
    }
};
var COUNT = /^\d+$/;
var count = function (text) { return (COUNT.test(text.trim()) ? Number(text) : null); };
export var limitsFromForm = function (values) { return ({
    messages_per_second: count(values.limits.messagesPerSecond),
    otp_reserved_per_second: count(values.limits.otpReservedPerSecond),
    otp_concurrency: count(values.limits.otpConcurrency),
    allowed_calling_codes: parseCallingCodes(values.limits.allowedCallingCodes).codes,
}); };
export var EAccountFormError;
(function (EAccountFormError) {
    EAccountFormError["REQUIRED"] = "REQUIRED";
    EAccountFormError["NOT_A_COUNT"] = "NOT_A_COUNT";
    EAccountFormError["OTP_ABOVE_TOTAL"] = "OTP_ABOVE_TOTAL";
    /** Codes sent at once: 0 would never send a code. */
    EAccountFormError["AT_LEAST_ONE"] = "AT_LEAST_ONE";
    EAccountFormError["INVALID_CALLING_CODE"] = "INVALID_CALLING_CODE";
    EAccountFormError["DUPLICATE_LANGUAGE"] = "DUPLICATE_LANGUAGE";
    EAccountFormError["NOT_A_URL"] = "NOT_A_URL";
    /** A removed provider: the account can't be saved, only replaced. */
    EAccountFormError["UNSUPPORTED_PROVIDER"] = "UNSUPPORTED_PROVIDER";
    /**
     * The address the credentials are sent to changed, and the credentials
     * weren't entered again. The backend refuses it with the same reason.
     */
    EAccountFormError["CREDENTIALS_FOLLOW_ENDPOINT"] = "CREDENTIALS_FOLLOW_ENDPOINT";
})(EAccountFormError || (EAccountFormError = {}));
/**
 * The sender field holding the address an account's credentials are sent to,
 * like `account_endpoint` in windmill: the Graph API base, the Infobip base or
 * the SMTP server. `null` for providers that name none.
 */
export var endpointField = function (provider) {
    switch (provider) {
        case EMessagingProvider.WHATSAPP_CLOUD_API:
        case EMessagingProvider.MESSENGER_SEND_API:
            return "api_base_url";
        case EMessagingProvider.VIBER_INFOBIP:
            return "base_url";
        case EMessagingProvider.SMTP:
            return "server_url";
        default:
            return null;
    }
};
var endpointOf = function (sender, key) { var _a; return ((_a = sender[key]) !== null && _a !== void 0 ? _a : "").trim().replace(/\/+$/, "") || null; };
/** Whether the form moves the account's credentials to another address. */
export var endpointChanged = function (original, values) {
    var key = endpointField(values.provider);
    return (!!original &&
        !!key &&
        original.provider === values.provider &&
        endpointOf(original.sender, key) !== endpointOf(values.sender, key));
};
/**
 * Credentials that must be entered again before the form can be saved: all
 * the provider needs to send, when the address they go to changed (the same
 * rule as `endpoint_change_refusal`). Empty otherwise.
 */
export var credentialsToReenter = function (original, values) {
    return endpointChanged(original, values)
        ? requiredCredentials(values.provider).filter(function (name) { return !values.credentials[name]; })
        : [];
};
// Provider endpoints carry the stored credentials, so they must be https.
var HTTPS_URL = /^https:\/\/\S+$/;
/** Errors by field path; empty when the form can be saved. */
export var validateAccountForm = function (values, 
/** The stored account, when editing one. */
original) {
    var errors = {};
    if (isUnsupportedProvider(values.provider)) {
        errors.provider = EAccountFormError.UNSUPPORTED_PROVIDER;
    }
    if (!values.name.trim()) {
        errors.name = EAccountFormError.REQUIRED;
    }
    for (var _i = 0, _a = senderFields(values.provider); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b.key, isRequired = _b.required;
        if (isRequired && !required(values, key)) {
            errors["sender.".concat(key)] = EAccountFormError.REQUIRED;
        }
    }
    var _c = values.limits, messagesPerSecond = _c.messagesPerSecond, otpReservedPerSecond = _c.otpReservedPerSecond, otpConcurrency = _c.otpConcurrency, allowedCallingCodes = _c.allowedCallingCodes;
    for (var _d = 0, _e = [
        ["messagesPerSecond", messagesPerSecond],
        ["otpReservedPerSecond", otpReservedPerSecond],
        ["otpConcurrency", otpConcurrency],
    ]; _d < _e.length; _d++) {
        var _f = _e[_d], key = _f[0], text = _f[1];
        if (text.trim() && count(text) === null) {
            errors["limits.".concat(key)] = EAccountFormError.NOT_A_COUNT;
        }
    }
    var total = count(messagesPerSecond);
    var reserved = count(otpReservedPerSecond);
    if (total !== null && reserved !== null && reserved > total) {
        errors["limits.otpReservedPerSecond"] = EAccountFormError.OTP_ABOVE_TOTAL;
    }
    if (count(otpConcurrency) === 0) {
        errors["limits.otpConcurrency"] = EAccountFormError.AT_LEAST_ONE;
    }
    if (parseCallingCodes(allowedCallingCodes).invalid.length) {
        errors["limits.allowedCallingCodes"] = EAccountFormError.INVALID_CALLING_CODE;
    }
    if (values.provider === EMessagingProvider.VIBER_INFOBIP) {
        var seen_1 = new Set();
        values.viberTemplates.forEach(function (row, index) {
            var language = row.language.trim();
            var key = "".concat(row.purpose, ":").concat(language);
            if (!language || !row.templateId.trim()) {
                errors["viberTemplates.".concat(index)] = EAccountFormError.REQUIRED;
            }
            else if (seen_1.has(key)) {
                errors["viberTemplates.".concat(index)] = EAccountFormError.DUPLICATE_LANGUAGE;
            }
            seen_1.add(key);
        });
    }
    var _loop_1 = function (key) {
        var url = optional(values, key);
        if (senderFields(values.provider).some(function (candidate) { return candidate.key === key; }) &&
            url &&
            !HTTPS_URL.test(url)) {
            errors["sender.".concat(key)] = EAccountFormError.NOT_A_URL;
        }
    };
    for (var _g = 0, _h = ["api_base_url", "base_url"]; _g < _h.length; _g++) {
        var key = _h[_g];
        _loop_1(key);
    }
    var endpoint = endpointField(values.provider);
    if (endpoint &&
        !errors["sender.".concat(endpoint)] &&
        credentialsToReenter(original, values).length) {
        errors["sender.".concat(endpoint)] = EAccountFormError.CREDENTIALS_FOLLOW_ENDPOINT;
    }
    return errors;
};
/** The variables of `upsert_messaging_account` for the form. */
export var upsertAccountVariables = function (id, values) { return ({
    id: id,
    channel: values.channel,
    name: values.name.trim(),
    sender: senderFromForm(values),
    limits: limitsFromForm(values),
    providerApproval: requiresProviderApproval(values.provider, values.channel)
        ? values.providerApproval
        : null,
    isDefault: values.isDefault,
    credentials: credentialsPayload(values),
}); };
/** Typed credentials to replace, or null when none was typed. */
export var credentialsPayload = function (values) {
    var entries = typedCredentials(values.provider)
        .map(function (name) { var _a; return [name, (_a = values.credentials[name]) !== null && _a !== void 0 ? _a : ""]; })
        .filter(function (_a) {
        var value = _a[1];
        return value.length > 0;
    });
    return entries.length ? Object.fromEntries(entries) : null;
};
/** Readiness always comes from the provider's check. */
export var accountReadiness = function (account) {
    var _a;
    var _b, _c;
    var capabilities = accountCapabilities(account);
    var readiness = function (purpose) {
        return purposeReadiness(account.provider, capabilities, account.provider_approval, account.status, purpose, null, accountIdentity(account.sender));
    };
    return {
        // A check of another identity (an earlier number or Page) says
        // nothing about the account as it is now.
        connected: ((_c = (_b = account.status) === null || _b === void 0 ? void 0 : _b.connected) !== null && _c !== void 0 ? _c : false) &&
            !isUnsupportedProvider(account.provider) &&
            !checkedForAnotherIdentity(account),
        purposes: (_a = {},
            _a[EMessagePurpose.OTP] = readiness(EMessagePurpose.OTP),
            _a[EMessagePurpose.NOTICE] = readiness(EMessagePurpose.NOTICE),
            _a),
    };
};
export var requiresProviderApproval = function (provider, channel) { var _a, _b; return (_b = (_a = providerCapabilities(provider, channel)) === null || _a === void 0 ? void 0 : _a.requires_provider_approval) !== null && _b !== void 0 ? _b : false; };
